import { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { useSalon } from "../context/SalonContext";
import { useLocationSwitcher } from "./useLocationSwitcher";
import {
  getOwnedCandidateLocations,
  hasOwnerOnboardingRedirectRun,
  isInitialOnboardingIncomplete,
  markOwnerOnboardingRedirectRun,
  pickMostRecentlyCreated,
  LocationOnboardingProbe,
} from "../utils/locationOnboarding";
import type { AvailableLocation } from "../types/featureAccess";

export type OwnerOnboardingRedirectStatus =
  | "idle" // not yet evaluated (or disabled)
  | "checking" // probing owned locations' onboarding state
  | "redirecting" // target found; location switch (PATCH + reload) in flight
  | "none"; // no redirect — land normally

/**
 * Owner login redirect to incomplete-location onboarding
 * (DESIGN_DELTA_2026-07-23 §4, PR #356). Used by LandingRedirect.
 *
 * When `enabled` (caller passes: flag ON via available_locations, Owner,
 * selected location fully onboarded), probes the OTHER locations this user
 * owns via the existing `GET /onboardings?salonId=<salon_uuid>` endpoint
 * (see src/utils/locationOnboarding.ts for the data-source rationale). If one
 * has incomplete initial onboarding, switches into it with the standard
 * location switch (PATCH /staff-users/me + full reload) — after the reload,
 * `/access` is scoped to that location and the existing LandingRedirect
 * onboarding gate resumes sub-flow B at the saved stage.
 *
 * NOT a hard gate: a sessionStorage guard is set before the switch, so the
 * redirect fires at most once per tab session — the owner can escape to a
 * completed location via the LocationPicker without being yanked back.
 */
export function useOwnerOnboardingRedirect(enabled: boolean): {
  status: OwnerOnboardingRedirectStatus;
} {
  const { getOnboardingProgress } = useApp();
  // SalonContext is untyped JS; cast (the established consumer pattern).
  const { availableLocations, selectedSalonId, businessInfo } = useSalon() as {
    availableLocations: AvailableLocation[] | undefined;
    selectedSalonId: number | null;
    businessInfo: { id?: string | number } | undefined;
  };
  const { switchLocation } = useLocationSwitcher();
  const [status, setStatus] = useState<OwnerOnboardingRedirectStatus>("idle");
  // Once-only latch: also protects against StrictMode double-mount and
  // re-renders while the async probe is in flight.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || startedRef.current) return;

    const candidates = getOwnedCandidateLocations(
      availableLocations ?? [],
      selectedSalonId ?? null,
      businessInfo?.id != null ? String(businessInfo.id) : null,
    );
    if (candidates.length === 0 || hasOwnerOnboardingRedirectRun()) {
      startedRef.current = true;
      setStatus("none");
      return;
    }

    startedRef.current = true;
    setStatus("checking");
    (async () => {
      const probes: LocationOnboardingProbe[] = await Promise.all(
        candidates.map(async (location) => {
          try {
            return {
              location,
              progress: await getOnboardingProgress(location.salon_uuid),
            };
          } catch {
            // Probe failure never blocks login — treat as not incomplete.
            return { location, progress: null };
          }
        }),
      );
      const target = pickMostRecentlyCreated(
        probes.filter(isInitialOnboardingIncomplete),
      );
      if (!target) {
        // Mark the guard on the "none" outcome too: the probe round ran and
        // found nothing, so re-mounting "/" later this session must not
        // re-probe GET /onboardings × N for a fully-onboarded owner.
        markOwnerOnboardingRedirectRun();
        setStatus("none");
        return;
      }
      setStatus("redirecting");
      // Guard BEFORE the switch: it must survive the reload so the owner can
      // later escape to a completed location without being re-redirected.
      markOwnerOnboardingRedirectRun();
      const switched = await switchLocation(target.location.salon_id);
      if (!switched) {
        // PATCH failed → no reload is coming. Stand down so LandingRedirect
        // stops holding its "redirecting" LoadingOverlay forever (the switcher
        // already surfaced an error toast); the user lands normally.
        setStatus("none");
      }
    })();
  }, [
    enabled,
    availableLocations,
    selectedSalonId,
    businessInfo,
    getOnboardingProgress,
    switchLocation,
  ]);

  return { status };
}

export default useOwnerOnboardingRedirect;
